/* Copyright (c) 2020-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import {
    AfterViewInit,
    Directive,
    ElementRef,
    HostListener,
    Optional,
    Input,
    NgZone,
    OnDestroy,
    Output,
    EventEmitter,
    Inject
} from '@angular/core';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatChipRow, MatChipSet } from '@angular/material/chips';
import { MatExpansionPanelHeader } from '@angular/material/expansion';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { MatRadioButton } from '@angular/material/radio';
import { MatSelect } from '@angular/material/select';
import { MatSlideToggle, MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatSlider, MatSliderThumb } from '@angular/material/slider';
import { MatTabGroup } from '@angular/material/tabs';
import { MatOption } from '@angular/material/core';
import { fromEvent, merge, Observable, Subject } from 'rxjs';
import {
    delay,
    distinctUntilChanged,
    filter,
    groupBy,
    mergeMap,
    takeUntil,
    tap,
    throttleTime,
    withLatestFrom
} from 'rxjs/operators';
import { isBackKey } from 'utils/lib/web-events/keyboard-event';
import { InputModalityService } from 'input-modality/lib/input-modality.service';
import { NvInputModality } from 'input-modality/lib/input-modality.type';
import { AccessibilityService } from 'crimson/lib/accessibility/accessibility.service';
import { LocaleService } from 'crimson/lib/locale-service/locale.service';
import { NV_HIG_CONFIG, NvHigConfig } from '../hig.config';
import { FocusManager } from '../services/focus-manager';
import { SpatialNavigationService } from '../services/spatial-navigation/spatial-navigation.service';

/**
 * Translates Enter/Space to a click event for non-button elements
 */
@Directive({
    selector: '[nvAddKeyboardActivate], mat-radio-button, mat-checkbox, a:not([href])',
    standalone: true
})
export class NvAddKeyboardActivateDirective {
    constructor(
        private el: ElementRef,
        @Optional() private radio: MatRadioButton,
        @Optional() private slide: MatSlideToggle,
        @Optional() private check: MatCheckbox,
        @Optional() private chipRow: MatChipRow,
        private inputModalityService: InputModalityService,
        @Optional() @Inject(NV_HIG_CONFIG) private config: NvHigConfig
    ) {}

    @HostListener('keydown', ['$event'])
    keydownEvent(event: KeyboardEvent) {
        const targetElement =
            this.radio?._inputElement?.nativeElement ??
            this.slide?._switchElement.nativeElement ??
            this.check?._inputElement.nativeElement ??
            this.chipRow?.primaryAction?._elementRef.nativeElement ??
            this.el.nativeElement;

        if (event.target !== targetElement) {
            return;
        }

        if (event.key === 'Enter' || event.key === ' ') {
            if (this.radio) {
                this.radio._onInputInteraction(event);
            } else if (this.check) {
                this.check._onInputClick();
            } else {
                const activatedByGamepad = this.inputModalityService.lastInputModality === NvInputModality.Gamepad;
                if (this.config?.customClickHandler && activatedByGamepad) {
                    this.config.customClickHandler(this.el.nativeElement);
                } else {
                    targetElement.click();
                }
            }
            event.preventDefault();
            event.stopPropagation();
        }
    }
}

/**
 * Make mat-option act as a simple clickable button. The directive prevent the mat-option's
 * default click handler to change the selected value.
 */
@Directive({
    selector: 'mat-option[nvMapOptionNoSelect]',
    standalone: true
})
export class NvMapOptionNoSelectDirective implements AfterViewInit, OnDestroy {
    @Output() optionClicked = new EventEmitter<PointerEvent | KeyboardEvent>();
    @Output() onEnterKeydown = new EventEmitter<KeyboardEvent>();

    constructor(
        private matOption: MatOption,
        private inputModalityService: InputModalityService
    ) {}

    ngAfterViewInit(): void {
        // make nvMapOptionNoSelect's _selectViaInteraction a no op so clicking it won't invoke its
        // original click handler
        this.matOption._selectViaInteraction = () => {};
        // Forward Enter to `onEnterKeydown` so the host component can react.
        this.matOption._handleKeydown = (keyEvent: KeyboardEvent) => {
            if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
                // Special gamepad handling only for Enter key
                if (
                    keyEvent.key === 'Enter' &&
                    this.inputModalityService.lastInputModality === NvInputModality.Gamepad
                ) {
                    this.onEnterKeydown.emit(keyEvent);
                    return;
                }
                // Default handling for all other cases (keyboard Enter/Space)
                this.handleHostClick(keyEvent);
            }
        };
        // Re-attach click listener; on browsers this path triggers `optionClicked`.
        this.matOption._getHostElement().addEventListener('click', this.handleHostClick);
    }

    /** Emits optionClicked for mouse/touch/keyboard click interactions */
    private handleHostClick = (evt: PointerEvent | KeyboardEvent): void => {
        this.optionClicked.emit(evt);
    };

    ngOnDestroy(): void {
        this.matOption._getHostElement().removeEventListener('click', this.handleHostClick);
    }
}

const KEYBOARD_OPEN_CLASS = 'nv-select-keyboard-open';

/**
 * Override the mat-select keyboard behavior to allow navigation when the select element is closed.
 */
@Directive({
    selector: 'mat-select[nvMatSelectKeyboardFixup]',
    standalone: true
})
export class NvMatSelectKeyboardFixupDirective implements AfterViewInit, OnDestroy {
    private destroy$ = new Subject<void>();
    private static openSelectCount = 0;
    private screenReaderEnabled = false;

    constructor(
        private select: MatSelect,
        private focusManager: FocusManager,
        private inputModalityService: InputModalityService,
        private accessibilityService: AccessibilityService,
        @Optional() @Inject(NV_HIG_CONFIG) private config: NvHigConfig
    ) {
        this.accessibilityService
            .onScreenReaderStateChanged()
            .pipe(takeUntil(this.destroy$))
            .subscribe(enabled => {
                this.screenReaderEnabled = enabled;
            });
    }

    @HostListener('click', ['$event'])
    onHostClick(event: MouseEvent) {
        if (!this.config?.screenReaderFocusClearWorkaround || !this.screenReaderEnabled || this.select.panelOpen) {
            return;
        }
        // Explicitly open on click when closed to handle platforms where default
        // click handling is intercepted by accessibility (e.g., Android TV with TalkBack).
        event.preventDefault();
        event.stopPropagation();
        this.select.focus();
        this.select.open();
    }

    ngAfterViewInit() {
        if (!this.select) {
            return;
        }
        setTimeout(() => {
            const tmp = this.select._handleKeydown.bind(this.select);
            this.select._handleKeydown = (event: KeyboardEvent) => {
                if (
                    !(this.select._elementRef.nativeElement as HTMLElement).classList.contains('cdk-keyboard-focused')
                ) {
                    this.focusManager.focusViaLastOrigin(this.select._elementRef.nativeElement);
                }
                if (isBackKey(event) && this.select.panelOpen) {
                    event.stopPropagation();
                    this.select.close();
                    setTimeout(() => {
                        this.select.focus();
                    });
                    // prevent polyfill from taking over and navigating outside of the panel
                } else if ((event.key === 'ArrowLeft' || event.key === 'ArrowRight') && this.select.panelOpen) {
                    event.stopPropagation();
                    event.preventDefault();
                } else if (this.select.panelOpen || event.key === 'Enter' || event.key === ' ') {
                    const hostEl = this.select._keyManager.activeItem?._getHostElement();
                    // Custom handling for nvMapOptionNoSelect elements
                    if (
                        hostEl &&
                        this.select.panelOpen &&
                        (event.key === 'Enter' || event.key === ' ') &&
                        hostEl.hasAttribute('nvMapOptionNoSelect')
                    ) {
                        if (event.key === ' ') {
                            hostEl.click();
                            return;
                        }
                        // Use configurable handler for platform-specific mat-option activation behavior
                        const activatedByGamepad =
                            this.inputModalityService.lastInputModality === NvInputModality.Gamepad;
                        if (this.config?.syntheticClickHandler && activatedByGamepad) {
                            this.config.syntheticClickHandler(hostEl);
                        } else {
                            // Default behavior: directly invoke click so `optionClicked` fires.
                            hostEl.click();
                        }
                        return;
                    }
                    tmp(event);
                    // When first option active via arrow keys, reset scroll
                    if (
                        (event.key === 'ArrowUp' || event.key === 'ArrowDown') &&
                        this.select.panelOpen &&
                        this.select._keyManager.activeItemIndex === 0 &&
                        this.select.panel
                    ) {
                        (this.select.panel.nativeElement as HTMLElement).scrollTop = 0;
                    }
                }
            };
        }, 150);

        if (this.select.openedChange?.pipe) {
            this.select.openedChange
                .pipe(
                    takeUntil(this.destroy$),
                    delay(0), // Defer until after Angular renders the panel
                    tap(opened => this.updatePanelKeyboardClass(opened))
                )
                .subscribe();
        }

        if (this.config?.screenReaderFocusClearWorkaround && this.select.openedChange?.subscribe) {
            this.select.openedChange
                .pipe(takeUntil(this.destroy$), withLatestFrom(this.accessibilityService.onScreenReaderStateChanged()))
                .subscribe(([opened, isScreenReaderEnabled]) => {
                    if (isScreenReaderEnabled) {
                        this.setFocusTrap(opened);
                        if (opened) {
                            this.setDialogFocus();
                        } else {
                            setTimeout(() => {
                                this.select.focus();
                            }, 50);
                        }
                    }
                });
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private setDialogFocus(): void {
        setTimeout(() => {
            const panel = document.querySelector('.mat-mdc-select-panel') as HTMLElement | null;
            if (!panel) {
                return;
            }

            const firstOption = panel.querySelector('mat-option') as HTMLElement | null;
            if (!firstOption) {
                return;
            }

            firstOption.setAttribute('tabindex', '0');
            this.focusManager.focusVia(firstOption, 'keyboard');
        });
    }

    private updatePanelKeyboardClass(opened: boolean): void {
        const origin = this.inputModalityService.lastFocusOrigin ?? 'keyboard';
        const isKeyboard = origin === 'keyboard';
        const panel = document.querySelector('.mat-mdc-select-panel') as HTMLElement | null;

        if (!panel) {
            return;
        }

        if (opened && isKeyboard) {
            panel.classList.add(KEYBOARD_OPEN_CLASS);
        } else {
            panel.classList.remove(KEYBOARD_OPEN_CLASS);
        }
    }

    private setFocusTrap(enabled: boolean): void {
        if (enabled) {
            setTimeout(() => {
                const panel = document.querySelector('.mat-mdc-select-panel') as HTMLElement | null;

                if (!panel) {
                    return;
                }

                panel.setAttribute('role', 'dialog');
                panel.setAttribute('aria-modal', 'true');

                if (panel.getAttribute('tabindex') === '-1') {
                    panel.removeAttribute('tabindex');
                }
            });
        } else {
            const panel = document.querySelector('.mat-mdc-select-panel') as HTMLElement | null;
            if (panel) {
                panel.removeAttribute('role');
                panel.removeAttribute('aria-modal');

                const options = panel.querySelectorAll('mat-option');
                options.forEach(option => {
                    if (option.getAttribute('tabindex') === '0') {
                        option.removeAttribute('tabindex');
                    }
                });
            }
        }
    }
}

/**
 * Override the mat-menu keyboard behavior and capture Escape/GoBack key to stop propagation
 */
@Directive({
    selector: 'mat-menu[nvMatMenuKeyboardFixup][menuTrigger]',
    standalone: true
})
export class NvMatMenuKeyboardFixupDirective implements AfterViewInit {
    @Input() menuTrigger: MatMenuTrigger;

    constructor(private menu: MatMenu) {}

    ngAfterViewInit() {
        if (!this.menu || !this.menuTrigger) {
            return;
        }
        const tmp = this.menu._handleKeydown.bind(this.menu);
        this.menu._handleKeydown = (event: KeyboardEvent) => {
            if (event.defaultPrevented) {
                return;
            }

            if (isBackKey(event) && this.menuTrigger.menuOpen) {
                event.preventDefault();
                event.stopPropagation();
                this.menuTrigger.closeMenu();
            } else {
                tmp(event);
            }
        };
    }
}

/**
 * Override the mat-menu focus behavior and capture ArrowLeft/ArrowRight key to stop propagation
 */
@Directive({
    selector: 'mat-menu[nvMatMenuFocusFixup]',
    standalone: true
})
export class NvMatMenuFocusFixupDirective implements AfterViewInit {
    constructor(
        private menu: MatMenu,
        private spatialNavigationService: SpatialNavigationService
    ) {}

    ngAfterViewInit() {
        if (!this.menu || !this.spatialNavigationService.enabled) {
            return;
        }
        const tmp = this.menu._handleKeydown.bind(this.menu);
        this.menu._handleKeydown = (event: KeyboardEvent) => {
            if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
                event.preventDefault();
            } else {
                tmp(event);
            }
        };
    }
}

/**
 * Override the mat-slider keyboard behavior to allow keyboard navigation
 * Fixes NVBugs 5700390: Enables gamepad input for sliders
 *
 * TODO: vertical sliders needed?
 */
@Directive({
    selector: 'mat-slider[nvMatSliderKeyboardFixup]',
    standalone: true
})
export class NvMatSliderKeyboardFixupDirective {
    // Inject MatSlider so we can manually delegate synthetic events (e.g., from game-pad)
    constructor(
        @Optional() private slider: MatSlider,
        private localeService: LocaleService
    ) {}

    @HostListener('keydown', ['$event'])
    onKeydown(event: KeyboardEvent): void {
        // For horizontal navigation keys (Left/Right) we want the slider to
        // process the event (so we DO NOT call preventDefault), but we still
        // stop propagation so that higher-level handlers such as the spatial
        // navigation service do not treat the key press as a navigation
        // command.

        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            /*
             * For real keyboard events Material slider processes the event itself before our
             * directive runs, so simply stopping propagation is enough. For gamepad navigation
             * however, the framework dispatches synthetic KeyboardEvents (isTrusted === false).
             * The internal MatSlider handler ignores un-trusted events, which means the slider
             * value stops updating once we swallow the event.  To keep the expected behaviour we
             * manually forward such events to the slider before calling stopPropagation().
             */
            if (!event.isTrusted && this.slider) {
                // END thumb on single-value slider (value thumb on range slider)
                const thumb: MatSliderThumb = this.slider._getInput?.call(this.slider, 2);
                if (!thumb) {
                    return;
                }

                // Respect RTL – arrow directions are flipped.
                const isRtl = this.localeService.isRTLLayoutEnabled;
                const moveRight = event.key === 'ArrowRight' ? !isRtl : isRtl;

                const host: HTMLInputElement | undefined = thumb._hostElement;
                if (!host) {
                    return;
                }

                // Native range-input helpers obey min / max / step automatically.
                moveRight ? host.stepUp() : host.stepDown();

                // Tell Material slider that the thumb (& hence value) changed.
                thumb._onInput();

                // NVBugs 5700390: Dispatch input event for parent component bindings
                host.dispatchEvent(new Event('input', { bubbles: true }));

                this.slider._onValueChange?.(thumb);
            }

            event.stopPropagation();
        } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            // Let the event bubble so the spatial navigation polyfill can handle it.
            return;
        }
    }
}

/**
 * Override the mat-chip-list keyboard behavior to allow keyboard navigation
 */
@Directive({
    selector: 'mat-chip-set[nvMatChipListKeyboardFixup]',
    standalone: true
})
export class NvMatChipListKeyboardFixupDirective implements AfterViewInit {
    constructor(private chipSet: MatChipSet) {}

    ngAfterViewInit() {
        if (!this.chipSet) {
            return;
        }
        setTimeout(() => {
            const tmp = this.chipSet._handleKeydown.bind(this.chipSet);
            this.chipSet._handleKeydown = (event: KeyboardEvent) => {
                if (
                    event.key !== 'ArrowUp' &&
                    event.key !== 'ArrowDown' &&
                    event.key !== 'ArrowLeft' &&
                    event.key !== 'ArrowRight'
                ) {
                    tmp(event);
                }
            };
        }, 150);
    }
}

const MAT_EXPANSION_ANIMATION_MS = 225;
const TOGGLE_THROTTLE_BUFFER_MS = 75;
const TOGGLE_THROTTLE_MS = MAT_EXPANSION_ANIMATION_MS + TOGGLE_THROTTLE_BUFFER_MS;

/**
 * Override the mat-expansion-panel-header keyboard behavior to allow keyboard navigation
 */
@Directive({
    selector: 'mat-expansion-panel-header[nvMatExpansionPanelHeaderKeyboardFixup]',
    standalone: true
})
export class NvMatExpansionPanelHeaderKeyboardFixupDirective implements AfterViewInit, OnDestroy {
    private destroy$ = new Subject<void>();

    constructor(
        private el: ElementRef<HTMLElement>,
        private panelHeader: MatExpansionPanelHeader
    ) {}

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngAfterViewInit() {
        if (!this.panelHeader) {
            return;
        }
        setTimeout(() => {
            const host = this.el.nativeElement;
            const originalKeydown = this.panelHeader._keydown.bind(this.panelHeader);
            const originalToggle = this.panelHeader._toggle.bind(this.panelHeader);

            this.panelHeader._keydown = (event: KeyboardEvent) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    event.stopPropagation();
                    return;
                }
                if (
                    event.key !== 'ArrowUp' &&
                    event.key !== 'ArrowDown' &&
                    event.key !== 'ArrowLeft' &&
                    event.key !== 'ArrowRight'
                ) {
                    originalKeydown(event);
                }
            };

            const keyDowns$ = fromEvent<KeyboardEvent>(host, 'keydown');
            const keyUps$ = fromEvent<KeyboardEvent>(host, 'keyup');

            merge(keyDowns$, keyUps$)
                .pipe(
                    filter(event => event.key === 'Enter' || event.key === ' '),
                    groupBy(event => event.key),
                    mergeMap(group$ => group$.pipe(distinctUntilChanged((prev, curr) => prev.type === curr.type))),
                    filter(event => event.type === 'keydown'),
                    throttleTime(TOGGLE_THROTTLE_MS, undefined, { leading: true, trailing: false }),
                    takeUntil(this.destroy$)
                )
                .subscribe(() => originalToggle());
        }, 150);
    }
}

/**
 * Override MatTabGroup's default behavior. The fixup does following things when spatial navigation
 * is enabled.
 *  1. MatTabGroup implements some keyboard interactions,
 *  refer to https://material.angular.io/components/tabs/overview#keyboard-interaction, which traps the focus
 *  in the label list. this fixup captures keydown event for navigation keys and dispatch them to spatial navigation service, so we can move
 *  focus to other focusable elements.
 *  2. MatTabGroup marks the tab label's tabindex to "-1" if the label is not activated, which makes moving focus to an inactivated tab label impossible.
 *  the fixup overrides this behavior by (1) select and update all elements, whose tabindex are "-1", to "0" when initialized (2) subscribe to the
 *  tabindex change in the subtree, and update the tabindex to "0" whenever the elements' tabindex is changed to "-1".
 */
@Directive({
    selector: 'mat-tab-group[nvMatTabGroupFixUp]',
    standalone: true
})
export class NvMatTabGroupFixupDirective implements AfterViewInit, OnDestroy {
    private destroy$: Subject<void> = new Subject();
    constructor(
        private tabGroup: MatTabGroup,
        private ngZone: NgZone,
        private spatialNavigationService: SpatialNavigationService
    ) {}

    ngAfterViewInit(): void {
        if (!this.spatialNavigationService.enabled) {
            return;
        }

        this.ngZone.runOutsideAngular(() => {
            if (!this.tabGroup?._elementRef?.nativeElement) {
                return;
            }

            this.updateAllTabIndex();

            this.observeTabIndexChanged$()
                .pipe(takeUntil(this.destroy$))
                .subscribe(record => {
                    const target = record.target as HTMLElement;
                    if (target.getAttribute('tabindex') === '-1') {
                        this.updateTabIndex(target);
                    }
                });

            fromEvent<KeyboardEvent>(this.tabGroup._elementRef.nativeElement, 'keydown', {
                capture: true
            })
                .pipe(
                    filter(
                        e =>
                            e.key === 'ArrowDown' ||
                            e.key === 'ArrowUp' ||
                            e.key === 'ArrowRight' ||
                            e.key === 'ArrowLeft'
                    ),
                    takeUntil(this.destroy$)
                )
                .subscribe(e => {
                    e.stopPropagation();

                    switch (e.key) {
                        case 'ArrowDown':
                            this.spatialNavigationService.navigate('down');
                            break;
                        case 'ArrowUp':
                            this.spatialNavigationService.navigate('up');
                            break;
                        case 'ArrowLeft':
                            this.spatialNavigationService.navigate('left');
                            break;
                        case 'ArrowRight':
                            this.spatialNavigationService.navigate('right');
                            break;
                    }
                });
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private updateAllTabIndex() {
        const programFocusable = (this.tabGroup._elementRef.nativeElement as HTMLElement).querySelectorAll(
            "[tabindex='-1']"
        );
        programFocusable.forEach(this.updateTabIndex);
    }

    private observeTabIndexChanged$(): Observable<MutationRecord> {
        return new Observable(sub => {
            const observer = new MutationObserver(records => {
                records.forEach(record => {
                    sub.next(record);
                });
            });

            observer.observe(this.tabGroup._elementRef.nativeElement, {
                attributes: true,
                subtree: true,
                // only observe the change of tabindex
                attributeFilter: ['tabindex']
            });

            return () => {
                observer.disconnect();
            };
        });
    }

    private updateTabIndex(element: HTMLElement) {
        element.setAttribute('tabindex', '0');
    }
}
